import { MoreOutlined, WarningFilled } from '@ant-design/icons'
import { Button, Dropdown, Menu, Tag, Tooltip } from 'antd'
import React from 'react'

import { renderCoupon, renderDate } from '../../../components/tables/table-column-renderers'
import {
  buildSearchFilterProps,
  sortDateColumn,
  sortNumericColumn,
  sortStringColumn,
} from '../../../components/tables/utils'
import { formatRating } from '../../../formatters/format-rating'
import { LongRatings, ShortRatings } from '../../../models'
import { Isin } from '../models'

const renderActionsMenu =
  ({
    onUpdateIsinRatings,
    onUpdateIsin,
  }: {
    onUpdateIsin: (isin: Isin) => void
    onUpdateIsinRatings: (isin: string) => void
  }) =>
  (_: any, isin: Isin) => {
    const menu = (
      <Menu>
        <Menu.Item key="updateIsin" onClick={() => onUpdateIsin(isin)}>
          Update Isin
        </Menu.Item>
        <Menu.Item key="updateIsinRatings" onClick={() => onUpdateIsinRatings(isin.isin)}>
          Update Isin Ratings
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

const renderRatings = ({
  short,
  long,
  hasIsinRating,
  hasIssuerAndIsinRatingEquals,
}: {
  short: ShortRatings
  long: LongRatings
  hasIsinRating?: boolean
  hasIssuerAndIsinRatingEquals?: boolean
}) => {
  let tagColor = ''

  if (hasIsinRating) {
    tagColor = 'default'
  }

  if (hasIssuerAndIsinRatingEquals) {
    tagColor = 'orange'
  }

  return (
    short && (
      <>
        <Tooltip title={`Short Rating ${hasIsinRating ? '(Overridden by Instrument Short Rating)' : ''}`}>
          <Tag color={tagColor || 'blue'}>{formatRating(short)}</Tag>
        </Tooltip>
        <Tooltip title={`Long Rating ${hasIsinRating ? '(Overridden by Instrument Long Rating)' : ''}`}>
          <Tag color={tagColor || 'purple'}>{formatRating(long)}</Tag>
        </Tooltip>
        {!hasIsinRating && hasIssuerAndIsinRatingEquals && (
          <Tooltip title="Instrument and issuer ratings are currently identical. If this ISIN should always have same rate as its issuer’s, you should remove the instrument rating">
            <WarningFilled className="warning" />
          </Tooltip>
        )}
      </>
    )
  )
}

export const buildIsinsTableColumns = ({ onUpdateIsinRatings, onUpdateIsin }) => [
  {
    title: 'ISIN',
    dataIndex: 'isin',
    sorter: sortStringColumn({ dataIndex: 'isin' }),
    ...buildSearchFilterProps({ dataIndex: 'isin' }),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    sorter: sortStringColumn({ dataIndex: 'type' }),
    ...buildSearchFilterProps({ dataIndex: 'type' }),
  },
  {
    title: 'Issuer',
    dataIndex: 'issuer',
    sorter: sortStringColumn({ dataIndex: 'issuer' }),
    render: (issuer: string, row: any) => <span>{row.issuerName ? `${row.issuerName} (${issuer})` : issuer}</span>,
    ...buildSearchFilterProps({ dataIndex: 'issuer' }),
  },
  {
    title: 'Maturity Date',
    dataIndex: 'maturity',
    sorter: sortDateColumn({ dataIndex: 'maturity' }),
    render: renderDate,
  },
  {
    title: 'Coupon',
    dataIndex: 'coupon',
    sorter: sortNumericColumn({ dataIndex: 'coupon' }),
    render: renderCoupon,
  },
  {
    title: 'FRN Issue Margin',
    dataIndex: 'frnIssueMargin',
    sorter: sortNumericColumn({ dataIndex: 'frnIssueMargin' }),
    render: renderCoupon,
  },
  {
    title: 'Issuer Rating',
    render: (isin: Isin) =>
      renderRatings({
        short: isin.issuerRatingShort,
        long: isin.issuerRatingLong,
        hasIsinRating: !!isin.isinRatingShort || !!isin.isinRatingLong,
        hasIssuerAndIsinRatingEquals:
          isin.issuerRatingLong === isin.isinRatingLong && isin.issuerRatingShort === isin.isinRatingShort,
      }),
  },
  {
    title: 'Instrument Rating',
    render: (isin: Isin) =>
      renderRatings({
        short: isin.isinRatingShort,
        long: isin.isinRatingLong,
        hasIssuerAndIsinRatingEquals:
          isin.issuerRatingLong === isin.isinRatingLong && isin.issuerRatingShort === isin.isinRatingShort,
      }),
  },
  { title: '', render: renderActionsMenu({ onUpdateIsinRatings, onUpdateIsin }) },
]
